/* ============================================================
   SAM-714 — shared pieces of the event editor, preview and list:
   status labels, month-written date formatting, and the form ⇄
   payload mapping used by both "Save" and "Publish".
   ============================================================ */
import type { Event, EventStatus } from '../../hooks/useEvents';

export const STATUS_META: Record<EventStatus, { label: string; bg: string; fg: string; dot: string }> = {
  draft: { label: 'Draft', bg: 'var(--warn-bg)', fg: 'var(--warn-fg)', dot: '#F59E0B' },
  published: { label: 'Published', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', dot: 'var(--ok-dot)' },
  archived: { label: 'Archived', bg: 'var(--line-subtle)', fg: 'var(--ink-500)', dot: 'var(--ink-400)' },
};

export const STATUS_ORDER: EventStatus[] = ['draft', 'published', 'archived'];

/** «Oct 3, 2026» — month written, never dd/mm or mm/dd. */
export const formatEventDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** SAM-1323 (E1): «Oct 29 – Nov 30, 2026» in one cell; a single day, or a missing end, is just the day. */
export const formatEventDates = (start?: string | null, end?: string | null): string => {
  const a = formatEventDate(start);
  const b = formatEventDate(end);
  if (!a || !b || a === b) return a || b;
  const yearOf = (iso?: string | null) => (iso ? new Date(iso).getFullYear() : NaN);
  if (yearOf(start) === yearOf(end)) return `${a.replace(/, \d{4}$/, '')} – ${b}`;
  return `${a} – ${b}`;
};

/** «Oct 3, 2026, 7:00 PM» */
export const formatEventDateTime = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${formatEventDate(iso)}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

/** end_date has no time zone and is Dominican Republic time (UTC-4, no DST): read it as such, not as the browser's. */
export const parseEventDate = (value: string): Date =>
  new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}-04:00`);

export const isEnded = (e: Pick<Event, 'end_date'>, now: Date = new Date()): boolean =>
  !!e.end_date && parseEventDate(e.end_date) < now;

/** SAM-1249: what the list shows; a published event whose end_date passed is «Finished» (not stored, so no status change). */
export type EventListStatus = EventStatus | 'finished';

export const FINISHED_META = { label: 'Finished', bg: 'var(--line-subtle)', fg: 'var(--ink-500)', dot: 'var(--ink-400)' };

export const listStatusOf = (status: EventStatus, e: Pick<Event, 'end_date'>, now: Date = new Date()): EventListStatus =>
  status === 'published' && isEnded(e, now) ? 'finished' : status;

/** Form state of the editor: the event's editable columns plus its status. */
/** One day of a multi-day event: its own start and end (HH:MM); a closed day is skipped (SAM-1072, behind VITE_EVENT_DAYS). */
export interface EventDayRow { date: string; start: string; end: string; closed: boolean }

export type EventForm = Partial<Event> & { status: EventStatus; days?: EventDayRow[] };

/** `+1 809 555 1072` → `+18095551072`: the phone is stored as E.164 with no spaces. */
export const compactPhone = (raw?: string | null): string => {
  const v = (raw ?? '').trim();
  if (!v) return '';
  const digits = v.replace(/[^\d]/g, '');
  return digits ? `${v.startsWith('+') ? '+' : ''}${digits}` : '';
};

/** Per-day hours are sent only once the backend has event_days (PR #740 plus Abel's migration). */
export const eventDaysEnabled = (): boolean => import.meta.env.VITE_EVENT_DAYS === 'true';

export const EMPTY_EVENT_FORM: EventForm = {
  name_es: '', name_en: '',
  short_description_es: '', short_description_en: '',
  description_es: '', description_en: '',
  address: '', start_date: '', end_date: '',
  capacity: 0,
  logo_url: '', background_img_url: '', banner_mobile_url: '',
  phone_number: '', instagram_url: '', website_url: '', pin_code: '',
  business_id: '',
  status: 'draft',
};

/** Loaded event → editor form (datetimes trimmed to what datetime-local accepts). */
export const eventToForm = (event: Event, status: EventStatus): EventForm => ({
  name_es: event.name_es || '', name_en: event.name_en || '',
  short_description_es: event.short_description_es || '', short_description_en: event.short_description_en || '',
  description_es: event.description_es || '', description_en: event.description_en || '',
  address: event.address || '',
  start_date: event.start_date ? event.start_date.slice(0, 16) : '',
  end_date: event.end_date ? event.end_date.slice(0, 16) : '',
  capacity: event.capacity || 0,
  logo_url: event.logo_url || '', background_img_url: event.background_img_url || '',
  banner_mobile_url: event.banner_mobile_url || '',
  phone_number: event.phone_number || '', instagram_url: event.instagram_url || '',
  website_url: event.website_url || '', pin_code: event.pin_code || '',
  business_id: event.business_id || '',
  tenant_id: event.tenant_id || undefined,
  access_settings_id: event.access_settings_id || undefined,
  event_type_id: event.event_type_id || undefined,
  event_group_id: event.event_group_id || undefined,
  status,
});

/**
 * Body for POST/PUT /crud/event. `active` is kept in step with the status
 * (active ⇔ published) for clients that still read it — the app and older
 * admin builds filter on `active`. published_at / published_by are stamped
 * by the CRUD lambda (SAM-713), never by the client.
 */
export const formToPayload = (form: EventForm, status: EventStatus): Partial<Event> => {
  const payload: Partial<Event> & { days?: unknown } = { ...form, status, active: status === 'published' };
  if (payload.phone_number) payload.phone_number = compactPhone(payload.phone_number);
  // days: [{date, start, end}] of the open days, only with the flag on; otherwise the event keeps its one start and end.
  if (eventDaysEnabled() && form.days?.length) {
    payload.days = form.days.filter((d) => !d.closed).map((d) => ({ date: d.date, start: d.start, end: d.end }));
  } else {
    delete payload.days;
  }
  delete payload.published_at;
  delete payload.published_by;
  delete payload.id;
  // SAM-737: the event code is generated by the server on create and cannot be changed.
  delete payload.pin_code;
  return payload;
};

/** The CRUD create response shape varies (`{id}`, `{data: {id}}`, `{data: [{id}]}`). */
export const createdEventId = (res: unknown): string | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = res as any;
  const id = r?.id ?? r?.data?.id ?? (Array.isArray(r?.data) ? r.data[0]?.id : undefined) ?? (Array.isArray(r) ? r[0]?.id : undefined);
  return id != null ? String(id) : null;
};

/** Event-shaped object for the public detail view, from editor data. */
export const formToPreviewEvent = (form: EventForm, id?: string, isPrivate = false) => ({
  ...form,
  id,
  name: form.name_es || form.name_en || '',
  // Show the Register button as guests will see it once the event is live.
  active: true,
  // SAM-730: lock + «Private event» label as on capcana.app. The code itself is never shown.
  is_private: isPrivate,
  pin_code: undefined,
});

/* ---------- Server sync (dev QA 01-10: stale status after Publish from Preview) ---------- */

/**
 * The editor's form after the server copy of the event changed (refetch after a
 * save, or a publish done from the Preview page).
 * - Form untouched since it was loaded (equals `baseline`) → take the server copy.
 * - Unsaved edits → keep them, but if the user did not touch Status, follow the
 *   server's status so a stale «Draft» never overwrites a newer «Published».
 */
export const syncFormWithServer = (form: EventForm, baseline: EventForm | null, server: EventForm): EventForm => {
  if (!baseline || JSON.stringify(form) === JSON.stringify(baseline)) return server;
  if (form.status === baseline.status && form.status !== server.status) return { ...form, status: server.status };
  return form;
};

/**
 * Status to send on save. `requested` is what the button asks for; if it is the
 * status the form was loaded with (the user did not pick another one) and the
 * server has moved on since, keep the server's status instead of reverting it.
 */
export const resolveSaveStatus = (
  requested: EventStatus,
  baselineStatus: EventStatus | undefined,
  serverStatus: EventStatus | undefined,
): EventStatus => {
  if (serverStatus && baselineStatus && requested === baselineStatus && serverStatus !== baselineStatus) return serverStatus;
  return requested;
};

/* ---------- Required fields (dev QA 01-10: silent validation) ---------- */

export type RequiredEventField = 'name' | 'business_id' | 'start_date' | 'end_date';

export interface MissingField {
  field: RequiredEventField;
  /** Short name used in «Fill in Business to publish». */
  label: string;
  /** Inline message under the field. */
  message: string;
}

/** Required fields still empty, in the order they appear on the page. */
export const missingRequiredFields = (
  form: Pick<EventForm, 'name_es' | 'name_en' | 'business_id' | 'start_date' | 'end_date'>,
): MissingField[] => {
  const missing: MissingField[] = [];
  if (!form.name_es?.trim() && !form.name_en?.trim()) {
    missing.push({ field: 'name', label: 'Name', message: 'Add a name in English or Spanish' });
  }
  if (!form.business_id) {
    missing.push({ field: 'business_id', label: 'Business', message: 'Select a business' });
  }
  // QA 07-10: saving without dates failed on the server and said nothing.
  if (!form.start_date) missing.push({ field: 'start_date', label: 'Start', message: 'Set a start date and time' });
  if (!form.end_date) {
    missing.push({ field: 'end_date', label: 'End', message: 'Set an end date and time' });
  } else if (form.start_date && form.end_date < form.start_date) {
    missing.push({ field: 'end_date', label: 'End', message: 'The end must be after the start' });
  }
  return missing;
};

/** «Fill in Name and Business to publish» */
export const missingFieldsHint = (missing: MissingField[], action: 'save' | 'publish'): string => {
  if (missing.length === 0) return '';
  const labels = missing.map((m) => m.label);
  const list = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0];
  return `Fill in ${list} to ${action}`;
};
